/**
 * UserRedemptions Component
 * 
 * Displays fulfillments (redemptions) for a user in a table format.
 * Table: Reward Name, Category Badge, Status Badge, Date, View link
 * Link to /fulfillments/:id
 * Shows max 10 recent redemptions with "View All" link
 * Empty state if none
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, type Column } from '@/components/common/Table';
import { FulfillmentStatusBadge, RewardCategoryBadge } from '@/components/common/Badge';
import type { FulfillmentResponse } from '@/app/types/fulfillment';
import './UserRedemptions.scss';

interface UserRedemptionsProps {
  fulfillments: FulfillmentResponse[];
  userId: number;
}

const MAX_DISPLAY_ITEMS = 10;

export const UserRedemptions: React.FC<UserRedemptionsProps> = ({ fulfillments, userId }) => {
  const navigate = useNavigate();

  // Limit display to most recent items (sorted by initiated_at descending)
  const displayedFulfillments = useMemo(() => {
    const sorted = [...fulfillments].sort((a, b) => {
      const dateA = new Date(a.initiated_at).getTime();
      const dateB = new Date(b.initiated_at).getTime();
      return dateB - dateA; // Most recent first
    });
    return sorted.slice(0, MAX_DISPLAY_ITEMS);
  }, [fulfillments]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: Column<FulfillmentResponse>[] = [
    {
      key: 'reward_name',
      header: 'Reward Name',
      width: '30%',
      render: (fulfillment) => fulfillment.reward_name || 'Unknown Reward',
    },
    {
      key: 'reward_category',
      header: 'Category',
      width: '15%',
      render: (fulfillment) => 
        fulfillment.reward_category ? (
          <RewardCategoryBadge category={fulfillment.reward_category} />
        ) : (
          'N/A'
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '15%',
      render: (fulfillment) => <FulfillmentStatusBadge status={fulfillment.status} />,
    },
    {
      key: 'initiated_at',
      header: 'Date',
      width: '20%',
      render: (fulfillment) => formatDate(fulfillment.initiated_at),
    },
    {
      key: 'actions',
      header: '',
      width: '20%',
      render: (fulfillment) => (
        <button
          className="btn-view"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/fulfillments/${fulfillment.fulfillment_id}`);
          }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="user-redemptions">
      <div className="user-redemptions-header">
        <h3>Redemptions</h3>
        {fulfillments.length > MAX_DISPLAY_ITEMS && (
          <a
            href={`#/fulfillments?user_id=${userId}`}
            className="user-redemptions-view-all-link"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/fulfillments?user_id=${userId}`);
            }}
          >
            View All Redemptions
          </a>
        )}
      </div>

      {fulfillments.length === 0 ? (
        <div className="user-redemptions-empty">
          <p>No redemptions found for this user.</p>
        </div>
      ) : (
        <>
          <Table<FulfillmentResponse>
            columns={columns}
            data={displayedFulfillments}
            keyExtractor={(fulfillment) => fulfillment.fulfillment_id}
            emptyMessage="No redemptions found"
          />
          {fulfillments.length > MAX_DISPLAY_ITEMS && (
            <div className="user-redemptions-footer">
              <a
                href={`#/fulfillments?user_id=${userId}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/fulfillments?user_id=${userId}`);
                }}
              >
                View all {fulfillments.length} redemptions →
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserRedemptions;

